import { useState, useRef, useCallback } from 'react';
import {
  XMarkIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';

interface SpreadsheetUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (topics: string[], mode: 'replace' | 'append') => void;
  existingTopicCount: number;
}

interface ParsedData {
  topics: string[];
  errors: { row: number; error: string }[];
  warnings: string[];
}

export default function SpreadsheetUploadModal({
  isOpen,
  onClose,
  onImport,
  existingTopicCount,
}: SpreadsheetUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [importMode, setImportMode] = useState<'replace' | 'append'>('replace');

  const resetState = useCallback(() => {
    setParsedData(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const parseFile = useCallback(async (file: File) => {
    setFileName(file.name);
    const errors: { row: number; error: string }[] = [];
    const warnings: string[] = [];
    let topics: string[] = [];

    try {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'csv') {
        // Parse CSV using native FileReader
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);

        if (lines.length === 0) {
          errors.push({ row: 0, error: 'File is empty' });
        } else {
          // Check if first line is a header
          const firstLine = lines[0].toLowerCase();
          const hasHeader = firstLine.includes('topic') || firstLine === 'topics';
          const startIndex = hasHeader ? 1 : 0;

          if (hasHeader) {
            warnings.push('Detected header row - skipping first line');
          }

          for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i];
            // Handle quoted values and trim
            let topic = line.replace(/^["']|["']$/g, '').trim();

            // If CSV has multiple columns, take the first one (topic column)
            if (topic.includes(',')) {
              const parts = topic.split(',');
              topic = parts[0].replace(/^["']|["']$/g, '').trim();
              if (parts.length > 1) {
                warnings.push(`Row ${i + 1}: Extra columns ignored (only topic column is used)`);
              }
            }

            if (topic) {
              topics.push(topic);
            } else {
              errors.push({ row: i + 1, error: 'Empty topic' });
            }
          }
        }
      } else if (extension === 'xlsx' || extension === 'xls') {
        // Parse Excel using xlsx library
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { header: 1 });

        if (data.length === 0) {
          errors.push({ row: 0, error: 'Spreadsheet is empty' });
        } else {
          // Check if first row is a header
          const firstRow = data[0] as unknown[];
          const firstCell = String(firstRow?.[0] || '').toLowerCase();
          const hasHeader = firstCell.includes('topic') || firstCell === 'topics';
          const startIndex = hasHeader ? 1 : 0;

          if (hasHeader) {
            warnings.push('Detected header row - skipping first row');
          }

          for (let i = startIndex; i < data.length; i++) {
            const row = data[i] as unknown[];
            const topic = String(row?.[0] || '').trim();

            if (topic) {
              topics.push(topic);
            } else if (row && row.length > 0) {
              errors.push({ row: i + 1, error: 'Empty topic in first column' });
            }
          }
        }
      } else {
        errors.push({ row: 0, error: `Unsupported file format: .${extension}. Please use .csv or .xlsx` });
      }

      // Deduplicate topics
      const uniqueTopics = [...new Set(topics)];
      if (uniqueTopics.length < topics.length) {
        warnings.push(`Removed ${topics.length - uniqueTopics.length} duplicate topic(s)`);
      }
      topics = uniqueTopics;

    } catch (err) {
      errors.push({ row: 0, error: `Failed to parse file: ${err instanceof Error ? err.message : 'Unknown error'}` });
    }

    setParsedData({ topics, errors, warnings });
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      parseFile(file);
    }
  }, [parseFile]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseFile(file);
    }
  }, [parseFile]);

  const handleImport = useCallback(() => {
    if (parsedData && parsedData.topics.length > 0) {
      onImport(parsedData.topics, importMode);
      handleClose();
    }
  }, [parsedData, importMode, onImport, handleClose]);

  const downloadTemplate = useCallback(() => {
    const csvContent = 'topic\nHow to find a trustworthy plumber\nWhat to look for when hiring a contractor\nBest questions to ask before hiring a service provider';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blog-topics-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <button
          type="button"
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
          aria-label="Close upload modal"
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Upload Topics from Spreadsheet
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {!parsedData ? (
              <>
                {/* Upload Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-brand bg-brand/5'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    aria-label="Upload topic spreadsheet file"
                  />
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    <span className="font-semibold text-brand">Click to upload</span> or drag and drop
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    CSV or Excel (.xlsx) files supported
                  </p>
                </div>

                {/* Format Info */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">File Format</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Your spreadsheet should have <strong>one topic per row</strong> in the first column.
                    Category, author, and status will use the settings from the form on the right.
                  </p>
                  <div className="bg-white border border-gray-200 rounded p-3 font-mono text-xs">
                    <div className="text-gray-500">topic</div>
                    <div>How to find a trustworthy plumber</div>
                    <div>What to look for when hiring a contractor</div>
                    <div>Best questions to ask before hiring...</div>
                  </div>
                  <button
                    onClick={downloadTemplate}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand hover:text-brand-dark"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download template CSV
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Parse Results */}
                <div className="space-y-4">
                  {/* File Info */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <DocumentArrowUpIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">{fileName}</span>
                    </div>
                    <button
                      onClick={resetState}
                      className="text-sm text-brand hover:text-brand-dark"
                    >
                      Choose different file
                    </button>
                  </div>

                  {/* Warnings */}
                  {parsedData.warnings.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          {parsedData.warnings.map((warning, i) => (
                            <div key={i}>{warning}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Errors */}
                  {parsedData.errors.length > 0 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-800">
                          <div className="font-medium mb-1">Errors found:</div>
                          {parsedData.errors.slice(0, 5).map((err, i) => (
                            <div key={i}>Row {err.row}: {err.error}</div>
                          ))}
                          {parsedData.errors.length > 5 && (
                            <div className="mt-1 text-red-600">
                              ...and {parsedData.errors.length - 5} more errors
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Success Summary */}
                  {parsedData.topics.length > 0 && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        <span className="text-sm font-medium text-green-800">
                          {parsedData.topics.length} topic{parsedData.topics.length !== 1 ? 's' : ''} ready to import
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Topics Preview */}
                  {parsedData.topics.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Preview (first 10 topics)
                      </h4>
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-48 overflow-y-auto">
                        {parsedData.topics.slice(0, 10).map((topic, i) => (
                          <div key={i} className="px-3 py-2 text-sm">
                            <span className="text-gray-400 mr-2">{i + 1}.</span>
                            {topic}
                          </div>
                        ))}
                        {parsedData.topics.length > 10 && (
                          <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                            ...and {parsedData.topics.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Import Mode Selection */}
                  {parsedData.topics.length > 0 && existingTopicCount > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Import Mode
                      </h4>
                      <p className="text-sm text-gray-500 mb-3">
                        You have {existingTopicCount} existing topic{existingTopicCount !== 1 ? 's' : ''}. How should we handle them?
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="importMode"
                            value="replace"
                            checked={importMode === 'replace'}
                            onChange={() => setImportMode('replace')}
                            className="h-4 w-4 text-brand focus:ring-brand"
                            aria-label="Replace all existing topics"
                          />
                          <span className="sr-only">Replace all existing topics</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">Replace all</div>
                            <div className="text-xs text-gray-500">Clear existing topics and use only uploaded ones</div>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="importMode"
                            value="append"
                            checked={importMode === 'append'}
                            onChange={() => setImportMode('append')}
                            className="h-4 w-4 text-brand focus:ring-brand"
                            aria-label="Append uploaded topics to existing topics"
                          />
                          <span className="sr-only">Append uploaded topics to existing topics</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">Append</div>
                            <div className="text-xs text-gray-500">
                              Add uploaded topics to existing ones ({existingTopicCount} + {parsedData.topics.length} = {existingTopicCount + parsedData.topics.length} total)
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!parsedData || parsedData.topics.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import {parsedData?.topics.length || 0} Topic{parsedData?.topics.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
